import {
  ChatRepositoryImpl,
  type ChatRepository
} from '@/chat/repositories/ChatRepository'
import type { ChatGroup } from '@/chat/domain/ChatGroup'
import { formatMessage } from '@/shared/utils/FormatMessageUtil'
import { ChannelTypes } from '../domain/ChannelTypes'

export interface ChatService {
  sendChatMessage: (
    chatGroup: ChatGroup,
    message: string,
    participantId: string,
    orderId: string,
    file?: File
  ) => Promise<void>
  putOnline: (
    jobId: string,
    particpantId: string,
    particpantName: string
  ) => Promise<void>
  signedImage: (imageUrl: string) => Promise<string[]>
  updatedSuggestion: (
    finalMessageSent: string,
    suggestionId: string,
    senderId: string,
    senderAction: string,
    jobId: string,
    channel: ChannelTypes
  ) => Promise<boolean | undefined>
  getAccessToken: (jobId: string, apiKey: string) => Promise<string | undefined>
}

export class ChatServiceImpl implements ChatService {
  private chatRepository: ChatRepository

  constructor() {
    this.chatRepository = new ChatRepositoryImpl()
  }

  public async sendChatMessage(
    chatGroup: ChatGroup,
    message: string,
    participantId: string,
    orderId: string,
    file?: File
  ): Promise<void> {
    try {
      let fileUrl = null

      if (file) {
        const url = await this.chatRepository.getUrlOfFile(file, orderId)
        if (url) fileUrl = url
      }
      await this.chatRepository.createChatMessage(
        chatGroup,
        message,
        participantId,
        orderId,
        fileUrl
      )
    } catch (error) {
      console.error(
        formatMessage(ChatServiceImpl.name, this.sendChatMessage.name, error)
      )
    }
  }

  public async putOnline(
    jobId: string,
    particpantId: string,
    particpantName: string
  ): Promise<void> {
    try {
      await this.chatRepository.putOnline(jobId, particpantId, particpantName)
    } catch (error) {
      console.error(
        formatMessage(ChatServiceImpl.name, this.putOnline.name, error)
      )
    }
  }

  public async signedImage(imageUrl: string): Promise<string[]> {
    try {
      return await this.chatRepository.signedImage(imageUrl)
    } catch (error) {
      console.error(
        formatMessage(ChatServiceImpl.name, this.signedImage.name, error)
      )
    }
  }

  public async updatedSuggestion(
    finalMessageSent: string,
    suggestionId: string,
    senderId: string,
    senderAction: string,
    jobId: string,
    channel: ChannelTypes
  ): Promise<boolean | undefined> {
    try {
      return await this.chatRepository.updatedSuggestion(
        finalMessageSent,
        suggestionId,
        senderId,
        senderAction.toUpperCase(),
        jobId,
        channel
      )
    } catch (error) {
      console.error(
        formatMessage(ChatServiceImpl.name, this.updatedSuggestion.name, error)
      )
    }
  }

  public async getAccessToken(
    jobId: string,
    apiKey: string
  ): Promise<string | undefined> {
    try {
      return await this.chatRepository.getAccessToken(jobId, apiKey)
    } catch (error) {
      console.error(
        formatMessage(ChatServiceImpl.name, this.getAccessToken.name, error)
      )
    }
  }
}
